import { toast } from 'react-toastify'

import { ExportToastBody } from 'components/pages/CustomerOrder/Orders/AdvanceSearchModal/ExportToastBody'
import {
  downloadFile,
  getFilenameFromContentDisposition,
} from 'utils/downloadFile'

/** Read a (possibly Blob) axios error body into a human-readable message. */
export async function readBlobError(error: any): Promise<string> {
  const data = error?.response?.data
  try {
    if (data instanceof Blob) {
      const text = await data.text()
      try {
        const parsed = JSON.parse(text)
        return parsed?.Message || parsed?.message || text || 'Export failed.'
      } catch {
        return text || 'Export failed.'
      }
    }
    return data?.Message || data?.message || error?.message || 'Export failed.'
  } catch {
    return error?.message || 'Export failed.'
  }
}

export interface RunExportWithToastOptions {
  /**
   * Kicks off the streamed file request. Must invoke the supplied onSuccess/onError
   * handlers once the request settles.
   *
   * BDL20-2286: wire this via a react-query `mutateAsync(vars).then(onSuccess).catch(onError)`,
   * NOT `mutate(vars, {onSuccess, onError})`. `onClose` below unmounts the calling modal, and
   * react-query v3's mutate()-level onSuccess/onError are delivered through the MutationObserver
   * owned by that component instance — once it unmounts, the observer is dropped from the
   * mutation's observer list and the callbacks never fire even though the request succeeded.
   * mutateAsync returns the underlying request promise directly, which resolves independently of
   * the component/observer lifecycle.
   */
  trigger: (handlers: {
    onSuccess: (res: any) => void
    onError: (err: any) => Promise<void> | void
  }) => void
  /** Filename used when the response has no Content-Disposition header. */
  defaultFilename: string
  /** Optional callback to close the host popup before the toast appears. */
  onClose?: () => void
}

/**
 * Standardised CSV/file export lifecycle (BDL20-2286):
 * close the popup -> persistent in-progress toast -> download the streamed
 * blob -> success toast; on error -> error toast + console.error.
 */
export function runExportWithToast(options: RunExportWithToastOptions): void {
  const { trigger, defaultFilename, onClose } = options

  /** Close the popup immediately on export. */
  onClose?.()

  /**
   * Show a persistent in-progress toast that stays until the streamed file is
   * fully downloaded.
   */
  const toastId = toast(<ExportToastBody status='progress' />, {
    position: 'top-right',
    autoClose: false,
    closeOnClick: false,
    closeButton: true,
    draggable: false,
    icon: false,
    className: 'toast--export',
  })

  trigger({
    onSuccess: (res: any) => {
      /** The streamed file arrives as a Blob; save it to the browser. */
      const blob: Blob = res?.data
      const filename =
        getFilenameFromContentDisposition(
          res?.headers?.['content-disposition'],
        ) ?? defaultFilename
      downloadFile(blob, filename)

      toast.update(toastId, {
        render: <ExportToastBody status='success' />,
        type: 'success',
        autoClose: 3000,
        closeOnClick: true,
        closeButton: true,
        draggable: true,
        icon: false,
        className: '',
      })
    },
    onError: async (err: any) => {
      const detail = await readBlobError(err)

      /** Detailed error to the browser console for debugging. */
      // eslint-disable-next-line no-console
      console.error('Export failed', {
        status: err?.response?.status,
        message: detail,
        err,
      })

      toast.update(toastId, {
        render: <ExportToastBody status='error' message={detail} />,
        type: 'error',
        autoClose: 5000,
        closeOnClick: true,
        closeButton: true,
        draggable: true,
        icon: false,
        className: '',
      })
    },
  })
}

export default runExportWithToast
